const RESET_CLASS = "sfe-reset-filters";

/** Same mark as the mobile reset control: a filter with an x. */
export const RESET_FILTERS_ICON_HTML =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5h11l-4.2 5.2V16l-2.6 1.4V10.2L3 5z"/><path d="m15 15 6 6"/><path d="m21 15-6 6"/></svg>';

function isWebHost(): boolean {
  return typeof document !== "undefined" && document.body.classList.contains("sfe-standalone-host");
}

function paintWebResetIcons(): void {
  if (!isWebHost()) return;
  for (const button of document.querySelectorAll<HTMLButtonElement>(`.${RESET_CLASS}`)) {
    if (!button.querySelector("svg")) button.innerHTML = RESET_FILTERS_ICON_HTML;
    button.setAttribute("aria-label", "Reset filters");
    button.title = "Reset filters";
  }
}
const FILTER_LABELS = new Set([
  "ticker",
  "type",
  "moneyness",
  "sector",
  "expiration",
  "expiry",
  "risk",
  "side",
  "status",
  "year",
  "rubric band",
  "earnings",
  "earning",
  "tradeable",
  "tradable",
  "collateral",
  "collat",
  "collat.",
  "quote",
  "dte",
  "shares",
  "starred",
  "columns",
  "show",
]);

const NAMED_FILTER_RES: { name: RegExp; allValue?: RegExp }[] = [
  { name: /^earnings?$/i },
  { name: /^trade?able$/i },
];

let mounted = false;
let observer: MutationObserver | null = null;
let syncTimer: ReturnType<typeof setTimeout> | null = null;

function directText(element: Element): string {
  return Array.from(element.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent ?? "")
    .join(" ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}


function findLabeledFilterRow(panel: HTMLElement): HTMLElement | null {
  const labels = Array.from(
    panel.querySelectorAll<HTMLElement>("label, span, div"),
  ).filter((element) => FILTER_LABELS.has(directText(element)));
  if (labels.length < 2) return null;

  const candidates = new Set<HTMLElement>();
  for (const label of labels) {
    let candidate = label.parentElement;
    while (candidate && candidate !== panel) {
      const contained = new Set(
        labels
          .filter((item) => candidate?.contains(item))
          .map((item) => directText(item)),
      );
      if (contained.size >= 2) {
        candidates.add(candidate);
        break;
      }
      candidate = candidate.parentElement;
    }
  }

  return (
    Array.from(candidates).sort(
      (a, b) =>
        a.querySelectorAll("*").length - b.querySelectorAll("*").length,
    )[0] ?? null
  );
}

function hasFilterControls(element: HTMLElement): boolean {
  return Boolean(
    element.querySelector(
      "select, input[type='search'], input[type='text'], button.active, [aria-pressed='true'], .year-chip, .sfe-watchlist-filter, #sfe-rubric-filter-host, #sfe-options-expiration-filter, #sfe-options-risk-filter, #sfe-calls-shares-filter, #sfe-stocks-shares-filter, #sfe-put-min-profit-filter, .sfe-calls-shares-filter, .sfe-stocks-shares-filter",
    ),
  );
}

function findNativeFilterBar(panel: HTMLElement): HTMLElement | null {
  for (const selector of [
    ":scope > .filter-bar",
    ":scope > .recs-filter-bar",
    ":scope > [class*='filter-bar']",
    ".filter-bar",
    ".recs-filter-bar",
    "[class*='filter-bar']",
  ]) {
    const row = panel.querySelector<HTMLElement>(selector);
    if (row && hasFilterControls(row)) return row;
  }
  return null;
}

function findFilterRow(panel: HTMLElement): HTMLElement | null {
  if (panel.classList.contains("sfe-watchlist-mode-panel")) {
    return panel.querySelector<HTMLElement>(".sfe-watchlist-toolbar-row");
  }

  if (panel.classList.contains("sfe-calls-plus-panel")) {
    return panel.querySelector<HTMLElement>(".sfe-calls-plus-filters");
  }

  if (
    panel.classList.contains("sfe-analytics-plus-panel") ||
    panel.classList.contains("sfe-tax-panel") ||
    panel.id === "sfe-analytics-panel"
  ) {
    return (
      panel.querySelector<HTMLElement>(".sfe-income-year-row") ??
      panel.querySelector<HTMLElement>(".sfe-income-year-chips")?.parentElement ??
      null
    );
  }

  const filterBar = findNativeFilterBar(panel);
  if (filterBar) return filterBar;

  const labeled = findLabeledFilterRow(panel);
  if (labeled && hasFilterControls(labeled)) return labeled;

  const selectors = [
    ":scope > [class*='filters-row']",
    ":scope > [class*='filter-row']",
    ".sfe-income-year-row",
  ];
  for (const selector of selectors) {
    const row = panel.querySelector<HTMLElement>(selector);
    if (row && hasFilterControls(row)) return row;
  }
  return null;
}

function panelFor(row: HTMLElement): HTMLElement {
  return (
    row.closest<HTMLElement>(
      "[data-wheel-view], .sfe-watchlist-mode-panel, .sfe-analytics-plus-panel, .sfe-tax-panel, #sfe-analytics-panel, #sfe-tax-panel, #sfe-focus-panel",
    ) ?? row
  );
}

function isFilterControl(node: Element): boolean {
  return !node.closest(
    "table, dialog, form, .sfe-income-month-selector, .sfe-income-month-detail, .sfe-income-days, .sfe-toolbar, .sfe-modal-overlay, [data-action='proxy-health']",
  );
}

function dispatchValueChange(control: HTMLInputElement | HTMLSelectElement): void {
  control.dispatchEvent(new Event("input", { bubbles: true }));
  control.dispatchEvent(new Event("change", { bubbles: true }));
}

function clickIfPresent(root: ParentNode, selector: string): void {
  root.querySelector<HTMLButtonElement>(selector)?.click();
}

function resetScope(panel: HTMLElement): HTMLElement {
  // Only widen to the parent when Earnings/Tradeable chrome lives there.
  const parent = panel.parentElement;
  if (!parent || parent === document.body || parent === document.documentElement) {
    return panel;
  }
  if (parent.querySelector(".sfe-toolbar, .sfe-root, [data-action='proxy-health']")) {
    return panel;
  }
  const hasNamed = Array.from(
    parent.querySelectorAll<HTMLElement>("label, span, div, legend"),
  ).some((el) => labelMatches(el, /^earnings?$/i) || labelMatches(el, /^trade?able$/i));
  return hasNamed ? parent : panel;
}

function labelMatches(element: Element, re: RegExp): boolean {
  const own = directText(element);
  if (own && re.test(own)) return true;
  const full = (element.textContent ?? "").replace(/\s+/g, " ").trim();
  return full.length > 0 && full.length <= 24 && re.test(full);
}

function findAllSelectOption(select: HTMLSelectElement): HTMLOptionElement | null {
  const options = Array.from(select.options);
  return (
    options.find((item) => /^all$/i.test(item.textContent?.trim() ?? "")) ??
    options.find((item) => /^all(?:\s|$)/i.test(item.textContent?.trim() ?? "")) ??
    options.find((item) => /^(all|any)$/i.test(item.value.trim()) || item.value.trim() === "") ??
    null
  );
}

function resetSelectToAll(select: HTMLSelectElement): void {
  const option = findAllSelectOption(select);
  if (!option || select.value === option.value) return;
  select.value = option.value;
  dispatchValueChange(select);
}

function isAllFilterButton(button: HTMLButtonElement): boolean {
  if (button.classList.contains(RESET_CLASS)) return false;
  if (!isFilterControl(button)) return false;
  if (
    button.matches(
      "[data-action='proxy-health'], .sfe-proxy-health, .sfe-modal-close, [data-action='close'], [data-action='save'], [data-action='test-api']",
    )
  ) {
    return false;
  }

  const rawValues = [
    button.dataset.filter,
    button.dataset.side,
    button.dataset.mny,
    button.dataset.earnings,
    button.dataset.tradeable,
    button.dataset.tradable,
    button.dataset.expirationFilter,
    button.dataset.riskFilter,
    button.getAttribute("value"),
  ].filter((value): value is string => value != null && value.trim() !== "");

  if (rawValues.some((value) => /^(all|any)$/i.test(value.trim()))) return true;

  const label = (button.textContent ?? "").replace(/\s+/g, " ").trim();
  return /^all$/i.test(label);
}

function resetButtonGroupToAll(host: HTMLElement): void {
  const allBtn = Array.from(host.querySelectorAll<HTMLButtonElement>("button")).find(
    isAllFilterButton,
  );
  allBtn?.click();
}

/** Explicitly reset Earnings / Tradeable — including the hidden Puts Earnings control. */
function resetNamedFilters(panel: HTMLElement): void {
  const root = resetScope(panel);

  for (const { name } of NAMED_FILTER_RES) {
    for (const el of root.querySelectorAll<HTMLElement>("label, span, div, legend")) {
      if (!labelMatches(el, name)) continue;

      let host: HTMLElement | null =
        el.closest<HTMLElement>(
          ".sfe-puts-earnings-filter-hidden, [class*='filter'], label",
        ) ?? el.parentElement;
      while (
        host &&
        host !== root &&
        !host.querySelector("select, button, input") &&
        host.parentElement
      ) {
        host = host.parentElement;
      }
      if (!host) continue;

      const labelled =
        el instanceof HTMLLabelElement && el.htmlFor
          ? document.getElementById(el.htmlFor)
          : null;
      const select =
        (labelled instanceof HTMLSelectElement ? labelled : null) ??
        host.querySelector<HTMLSelectElement>("select");
      if (select) {
        resetSelectToAll(select);
        continue;
      }
      resetButtonGroupToAll(host);
    }
  }

  for (const select of root.querySelectorAll<HTMLSelectElement>("select")) {
    const id = `${select.id} ${select.name} ${select.getAttribute("aria-label") ?? ""}`;
    if (!/(earning|trade?able)/i.test(id)) continue;
    resetSelectToAll(select);
  }
}

/** Extension-owned filters keep module state — invoke their own All handlers. */
function resetExtensionOwnedFilters(panel: HTMLElement): void {
  clickIfPresent(panel, ".sfe-rubric-filter-all");
  clickIfPresent(panel, '[data-expiration-filter="all"]');
  clickIfPresent(panel, '[data-risk-filter="all"]');
  clickIfPresent(panel, '[data-calls-shares="all"]');
  clickIfPresent(panel, '[data-stocks-shares="all"]');
  const callsTable = document.getElementById("wheelCallsTable");
  if (callsTable instanceof HTMLTableElement) {
    const callsView = callsTable.closest('[data-wheel-view="calls"]');
    if (callsView && panel.contains(callsTable)) {
      void import("./callsScreenerRisky").then(({ resetCallsRiskyFilters }) =>
        resetCallsRiskyFilters(callsTable),
      );
    }
  }
  clickIfPresent(panel, '.sfe-income-year-chips .sfe-year-chip--current');
  clickIfPresent(panel, '.year-chips [data-year="all"], .year-chips [data-year=""]');
  clickIfPresent(panel, '.sfe-watchlist-filter[data-action="filter"][data-filter="all"]');
  resetAnalyticsLocalFilters(panel);
}

/** Analytics(L) ticker/type live in module + localStorage — clearing the input is not enough unless input/change fire. */
function resetAnalyticsLocalFilters(panel: HTMLElement): void {
  const ticker = panel.querySelector<HTMLInputElement>("#sfeAnalyticsTicker");
  if (ticker?.value) {
    ticker.value = "";
    dispatchValueChange(ticker);
  }
  const type = panel.querySelector<HTMLSelectElement>("#sfeAnalyticsType");
  if (type && type.value !== "all") {
    type.value = "all";
    dispatchValueChange(type);
  }
}

function resetSelects(root: HTMLElement): void {
  for (const select of root.querySelectorAll<HTMLSelectElement>("select")) {
    if (!isFilterControl(select)) continue;
    resetSelectToAll(select);
  }
}

function resetTextInputs(root: HTMLElement): void {
  for (const input of root.querySelectorAll<HTMLInputElement>(
    "input[type='search'], input[type='text'], input[type='number'], input:not([type])",
  )) {
    if (!isFilterControl(input)) continue;
    if (!input.value) continue;
    input.value = "";
    dispatchValueChange(input);
  }

  // Collateral filters are sometimes plain number inputs identified only by label/id.
  for (const el of root.querySelectorAll<HTMLElement>("label, span, div, legend")) {
    if (!labelMatches(el, /^collat(?:eral)?\.?$/i)) continue;
    const labelled =
      el instanceof HTMLLabelElement && el.htmlFor
        ? document.getElementById(el.htmlFor)
        : null;
    const host = el.closest<HTMLElement>("label, [class*='filter']") ?? el.parentElement;
    const input =
      (labelled instanceof HTMLInputElement ? labelled : null) ??
      host?.querySelector<HTMLInputElement>("input");
    if (!input || !isFilterControl(input) || !input.value) continue;
    input.value = "";
    dispatchValueChange(input);
  }

  for (const input of root.querySelectorAll<HTMLInputElement>("input")) {
    const id = `${input.id} ${input.name} ${input.getAttribute("aria-label") ?? ""}`;
    if (!/collat/i.test(id)) continue;
    if (!isFilterControl(input) || !input.value) continue;
    input.value = "";
    dispatchValueChange(input);
  }
}

function nativeAllButtons(root: HTMLElement): HTMLButtonElement[] {
  return Array.from(root.querySelectorAll<HTMLButtonElement>("button")).filter(
    (button) => {
      // Extension adapters handle these explicitly.
      if (
        button.matches(
          "[data-expiration-filter], [data-risk-filter], .sfe-rubric-filter-all, .sfe-watchlist-filter, [data-year]",
        )
      ) {
        return false;
      }
      return isAllFilterButton(button);
    },
  );
}

function resetNativeButtonGroups(root: HTMLElement): void {
  const clickedParents = new Set<Element>();
  for (const button of nativeAllButtons(root)) {
    const parent = button.parentElement;
    if (parent && clickedParents.has(parent)) continue;
    if (parent) clickedParents.add(parent);
    button.click();
  }
}

function resetPanelFilters(row: HTMLElement): void {
  const panel = panelFor(row);

  const run = () => {
    const currentRow = findFilterRow(panel) ?? row;
    const scope = resetScope(panel);
    resetNamedFilters(panel);
    resetTextInputs(scope);
    resetSelects(scope);
    resetExtensionOwnedFilters(panel);
    resetNativeButtonGroups(currentRow);
    // Some native groups live outside the tightest labeled row.
    resetNativeButtonGroups(scope);
  };

  run();
  // Native controls can re-render after each change.
  requestAnimationFrame(run);
}

function resetButtonHost(row: HTMLElement): HTMLElement {
  return (
    row.querySelector<HTMLElement>(".sfe-income-year-actions") ??
    row.querySelector<HTMLElement>(".sfe-income-year-trailing") ??
    row
  );
}

function findExistingPanelReset(panel: HTMLElement): HTMLButtonElement | null {
  return panel.querySelector<HTMLButtonElement>(`.${RESET_CLASS}`);
}

/** Drop auto-injected duplicates when a native reset already lives on another filter row. */
function removeInjectedResetDuplicates(panel: HTMLElement): void {
  const resets = Array.from(panel.querySelectorAll<HTMLButtonElement>(`.${RESET_CLASS}`));
  if (resets.length <= 1) return;

  const keep =
    panel.querySelector<HTMLButtonElement>(
      `.sfe-filter-reset-actions .${RESET_CLASS}:not([data-sfe-injected])`,
    ) ??
    panel.querySelector<HTMLButtonElement>(`.${RESET_CLASS}:not([data-sfe-injected])`) ??
    resets[0];

  for (const button of resets) {
    if (button === keep) continue;
    if (button.dataset.sfeInjected === "true") {
      button.closest(".sfe-filter-reset-actions[data-sfe-injected]")?.remove();
    }
  }
}

function ensureResetButton(row: HTMLElement): void {
  const host = resetButtonHost(row);
  let button = host.querySelector<HTMLButtonElement>(`.${RESET_CLASS}`);
  if (!button) {
    button = row.querySelector<HTMLButtonElement>(`.${RESET_CLASS}`);
  }
  if (!button) {
    const group = document.createElement("div");
    group.className = "sfe-filter-reset-actions";
    group.setAttribute("data-sfe-injected", "true");
    button = document.createElement("button");
    button.type = "button";
    button.className = RESET_CLASS;
    if (isWebHost()) {
      button.innerHTML = RESET_FILTERS_ICON_HTML;
      button.setAttribute("aria-label", "Reset filters");
      button.title = "Reset filters";
    } else {
      button.textContent = "Reset Filters";
    }
    button.setAttribute("data-sfe-injected", "true");
    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      resetPanelFilters(row);
    });
    group.appendChild(button);
    host.appendChild(group);
  } else if (button.closest(".sfe-filter-reset-actions")) {
    // Leave grouped [☷][Reset] placement intact (web screeners, etc.).
  } else if (button.parentElement !== host) {
    host.appendChild(button);
  }

  const style = getComputedStyle(row);
  if (style.display === "block" || style.display === "inline") {
    row.style.display = "flex";
    row.style.flexWrap = "wrap";
    const panel = panelFor(row);
    row.style.alignItems =
      panel.classList.contains("sfe-analytics-plus-panel") ||
      panel.classList.contains("sfe-tax-panel")
        ? "center"
        : "flex-end";
    row.style.gap = row.style.gap || "12px";
  }
}

function isAgentsPanel(panel: HTMLElement): boolean {
  return (
    panel.getAttribute("data-wheel-view") === "agents" ||
    panel.classList.contains("sfe-agents-panel-host") ||
    Boolean(panel.closest(".sfe-agents-panel-host, .sfe-agents-panel"))
  );
}

function isTaxPanel(panel: HTMLElement): boolean {
  return (
    panel.getAttribute("data-wheel-view") === "tax" ||
    panel.classList.contains("sfe-tax-panel") ||
    panel.id === "sfe-tax-panel"
  );
}

function removeInjectedResetButtons(panel: HTMLElement): void {
  panel
    .querySelectorAll<HTMLElement>(`.sfe-filter-reset-actions[data-sfe-injected]`)
    .forEach((el) => el.remove());
  panel.querySelectorAll<HTMLButtonElement>(`.${RESET_CLASS}[data-sfe-injected]`).forEach((el) => {
    el.remove();
  });
}

export function syncResetButtons(): void {
  const panels = document.querySelectorAll<HTMLElement>(
    "[data-wheel-view], .sfe-watchlist-mode-panel, .sfe-analytics-plus-panel, .sfe-calls-plus-panel, #sfe-analytics-panel, #sfe-focus-panel",
  );
  for (const panel of panels) {
    if (!panel.isConnected) continue;
    // Agentic settings are not screener filters — skip auto "Reset Filters".
    if (isAgentsPanel(panel)) {
      removeInjectedResetButtons(panel);
      continue;
    }
    if (isTaxPanel(panel)) {
      removeInjectedResetButtons(panel);
      continue;
    }
    removeInjectedResetDuplicates(panel);
    const row = findFilterRow(panel);
    if (!row) continue;
    const existing = findExistingPanelReset(panel);
    if (existing && !row.contains(existing)) continue;
    ensureResetButton(row);
  }
  paintWebResetIcons();
}

function scheduleSync(immediate = false): void {
  if (syncTimer) clearTimeout(syncTimer);
  if (immediate) {
    syncResetButtons();
    return;
  }
  syncTimer = setTimeout(() => {
    syncTimer = null;
    syncResetButtons();
  }, 50);
}

function observeWheelViewVisibility(): void {
  const views = document.querySelectorAll<HTMLElement>("[data-wheel-view]");
  if (!views.length) return;

  const visibilityObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== "attributes") continue;
      if (mutation.attributeName === "class" || mutation.attributeName === "hidden") {
        scheduleSync(true);
        return;
      }
    }
  });

  for (const view of views) {
    visibilityObserver.observe(view, {
      attributes: true,
      attributeFilter: ["class", "hidden"],
    });
  }
}

export function mountFilterResetButtons(): void {
  scheduleSync(true);
  if (mounted) return;
  mounted = true;
  observeWheelViewVisibility();
  window.addEventListener("hashchange", () => scheduleSync(true), { passive: true });
  observer?.disconnect();
  observer = new MutationObserver(() => scheduleSync());
  observer.observe(document.body, { childList: true, subtree: true });
}
